/**
 * Feather Reel — the media-banner "Stories" experience.
 *
 * Ported 1:1 from iOS `BannerPromotion.swift` (`ReelBanner` / `ReelClip`) and Android
 * `ReelScreen`. A reel is a `banner-promotions` Firestore doc with `type === 'media'`, read
 * through `BannerService`. Unlike iOS — which hand-parses the Firestore REST typed-value
 * wrappers (`stringValue`, `arrayValue.values`, `mapValue.fields`) — `@angular/fire` hands us
 * already-decoded native objects, so these interfaces are the plain shapes `doc.data()` yields.
 *
 * NAMING: keys are camelCase, matching the Firestore field names exactly (same convention as the
 * existing `BannerPromotion` interface — the web repo maps no field names).
 * NULLABILITY: every media field is OPTIONAL — present only on `type === 'media'` docs, and even
 * then individual fields may be blank. `toReelBanner` / `resolveReelText` apply the iOS fallback
 * rules (see the parity notes on each).
 */

/** A `{ langCode: text }` localized map, as stored in the marketing Firestore docs. */
export type LocalizedMap = Record<string, string>;

/**
 * Raw media item as decoded from a `banner-promotions` doc's `mediaItems` array — the shape before
 * `toReelClip` resolves it. Mirrors iOS `FirebaseMediaFieldsModel` (per-clip reel additions).
 */
export interface MediaItem {
  mediaUrl?: string;
  mediaThumbnailUrl?: string;
  /** "video" (case-insensitive) => video; anything else / absent => image. */
  mediaType?: string;
  /** "portrait" (or absent) => portrait; "landscape" => landscape. */
  orientation?: string;
  /** Seconds. Absent / non-positive => {@link DEFAULT_CLIP_DURATION_SECONDS}. */
  duration?: number;
  title?: LocalizedMap;
  description?: LocalizedMap;
  teaser?: LocalizedMap;
}

/**
 * The media-only fields a `type: 'media'` `banner-promotions` doc carries on top of the base
 * promotion fields. `BannerService.BannerPromotion` intersects this; `toReelBanner` consumes it.
 */
export interface MediaBannerFields {
  detailsImageUrl?: string;
  rewardImageUrl?: string;
  brandLogoUrl?: string;
  /** Free text shown in the price pill, e.g. "€4.50" — a label, not a number. */
  price?: string;
  /** Selects the end-card gradient theme (case-insensitive; unknown/blank => RED). */
  backgroundType?: string;
  /** `app-catalogs` doc id for the end-card marquee; the catalog is fetched lazily. */
  endCardCatalogId?: string;
  detailTitle?: LocalizedMap;
  detailDescription?: LocalizedMap;
  actionButtonText?: LocalizedMap;
  secondaryButtonText?: LocalizedMap;
  rewardName?: LocalizedMap;
  rewardInstructions?: LocalizedMap;
  mediaItems?: MediaItem[];
}

/** One resolved clip — a single video or image slide. */
export interface ReelClip {
  /** Required and non-empty; items without it are dropped by {@link toReelBanner}. */
  mediaUrl: string;
  /** Poster for videos (also shown as the fallback still if a video fails). */
  thumbnailUrl?: string;
  isVideo: boolean;
  isPortrait: boolean;
  /** Seconds an image shows for (and how long a failed video's poster shows). */
  durationSeconds: number;
  /** Localized maps kept raw; resolved per current language at render via {@link resolveReelText}. */
  title?: LocalizedMap;
  description?: LocalizedMap;
  teaser?: LocalizedMap;
}

/** The resolved media banner that drives the reel. */
export interface ReelBanner {
  /** The `banner-promotions` doc id (for analytics parity). */
  bannerId: string;
  /** Clips in Firestore array order — NEVER re-sorted (iOS parity). */
  clips: ReelClip[];

  // ---- scalars ----
  price?: string;
  backgroundType?: string;
  endCardCatalogId?: string;

  // ---- image URLs ----
  detailsImageUrl?: string;
  brandLogoUrl?: string;
  /** iOS `panelImageURL`: `rewardImageUrl` if non-empty, else `detailsImageUrl`. */
  panelImageUrl?: string;

  // ---- localized maps (resolved lazily via resolveReelText) ----
  detailTitle?: LocalizedMap;
  detailDescription?: LocalizedMap;
  actionButtonText?: LocalizedMap;
  secondaryButtonText?: LocalizedMap;
  rewardName?: LocalizedMap;
  rewardInstructions?: LocalizedMap;
}

/** One end-card marquee card, resolved from an `app-catalogs` doc's `items`. */
export interface CatalogItem {
  imageUrl: string;
  label?: string;
  order?: number;
}

/** iOS `ReelClip.defaultDurationSeconds`. */
export const DEFAULT_CLIP_DURATION_SECONDS = 5;

/**
 * Resolve one raw `mediaItems` entry into a {@link ReelClip}, or `null` when it must be dropped.
 *
 * iOS parity (`ReelClip.init?`): a blank `mediaUrl` drops the clip; `duration` defaults to 5;
 * `isVideo` is a case-insensitive `mediaType === 'video'`; `isPortrait` is true when `orientation`
 * is absent or (case-insensitively) `"portrait"`.
 */
export function toReelClip(item: MediaItem | null | undefined): ReelClip | null {
  const mediaUrl = (item?.mediaUrl ?? '').trim();
  if (!item || !mediaUrl) return null;

  const orientation = (item.orientation ?? '').toLowerCase();
  const duration =
    typeof item.duration === 'number' && item.duration > 0
      ? item.duration
      : DEFAULT_CLIP_DURATION_SECONDS;

  return {
    mediaUrl,
    thumbnailUrl: (item.mediaThumbnailUrl ?? '').trim() || undefined,
    isVideo: (item.mediaType ?? '').toLowerCase() === 'video',
    isPortrait: orientation === '' || orientation === 'portrait',
    durationSeconds: duration,
    title: item.title,
    description: item.description,
    teaser: item.teaser,
  };
}

/**
 * Build a {@link ReelBanner} from a decoded `type: 'media'` doc, or `null` if `doc` is absent.
 * `clips` keeps Firestore array order and drops any entry with a blank `mediaUrl` (iOS parity).
 * The returned banner may have `clips.length === 0` — callers must treat an empty reel as inert.
 */
export function toReelBanner(
  doc: ({ id?: string } & MediaBannerFields) | null | undefined
): ReelBanner | null {
  if (!doc) return null;

  const clips = (doc.mediaItems ?? [])
    .map(toReelClip)
    .filter((c): c is ReelClip => c !== null);

  const rewardImage = (doc.rewardImageUrl ?? '').trim();
  const detailsImage = (doc.detailsImageUrl ?? '').trim();

  return {
    bannerId: doc.id ?? '',
    clips,
    price: doc.price,
    backgroundType: doc.backgroundType,
    endCardCatalogId: doc.endCardCatalogId,
    detailsImageUrl: detailsImage || undefined,
    brandLogoUrl: (doc.brandLogoUrl ?? '').trim() || undefined,
    panelImageUrl: rewardImage || detailsImage || undefined,
    detailTitle: doc.detailTitle,
    detailDescription: doc.detailDescription,
    actionButtonText: doc.actionButtonText,
    secondaryButtonText: doc.secondaryButtonText,
    rewardName: doc.rewardName,
    rewardInstructions: doc.rewardInstructions,
  };
}

/**
 * Resolve a localized map for the reel — the exact iOS `reelLocalized` chain:
 * current language → `en` → first non-blank value in the map → the provided `fallback`.
 *
 * Pass `currentLang` from `LanguageService.getCurrentLang()` and `fallback` from the ngx-translate
 * `reel.*` string. This mirrors `LanguageService.localizeMap` but adds the iOS "first non-blank"
 * tier before the fallback, so a map that carries only some other language still shows real text.
 */
export function resolveReelText(
  map: LocalizedMap | null | undefined,
  currentLang: string,
  fallback: string
): string {
  if (map && typeof map === 'object') {
    const current = map[currentLang];
    if (current && current.trim()) return current;

    const english = map['en'];
    if (english && english.trim()) return english;

    for (const key of Object.keys(map)) {
      const value = map[key];
      if (value && value.trim()) return value;
    }
  }
  return fallback;
}
